import { Col, Row } from 'react-bootstrap';
import { observer } from 'mobx-react-lite';

import { EditBoolean } from 'components/forms/EditBoolean';
import { EditNumber } from 'components/forms/EditNumber';
import { EditRadioGroup } from 'components/forms/EditRadioGroup';
import { EditSelect, type EditSelectOptionList } from 'components/forms/EditSelect';
import { EditString } from 'components/forms/EditString';
import { EditTime } from 'components/forms/EditTime';
import { EditSchedulingWindowReference } from 'edit/reference/EditSchedulingWindowReference';
import { EditTimeZoneReference } from 'edit/reference/EditTimeZoneReference';

import type { EditableRecurrence, IntervalRecurrenceUnit, RecurrenceMode } from './EditableRecurrence';

// 4 of the 7 interval units exposed in this UI — matches the original modal's
// `UNIT_OPTIONS`. Adding week/month/year is a UX change PM owns.
const UNIT_OPTIONS: EditSelectOptionList<IntervalRecurrenceUnit> = [
  { key: 'second', value: 'second', label: 'Seconds' },
  { key: 'minute', value: 'minute', label: 'Minutes' },
  { key: 'hour', value: 'hour', label: 'Hours' },
  { key: 'day', value: 'day', label: 'Days' },
];

interface Props {
  readonly recurrence: EditableRecurrence;
  /**
   * Hide the per-rule description input. Omitted (false) shows it — the edit
   * dialog wants it; the node-submit inline rule sets this, since a description
   * adds no value for a one-off ad-hoc schedule.
   */
  readonly hideDescription?: boolean;
}

/**
 * The fields of a single recurrence rule. A pure `observer` view over an
 * `EditableRecurrence`: every control reads a getter and writes through a bound
 * setter, so there is no local state and no business logic here — toggling the
 * mode just sets `mode`, and the model decides what that means at save time.
 */
export const RecurrenceRuleFields = observer<Props>(({ recurrence, hideDescription }) => (
  <>
    {!hideDescription && (
      <Row className="mb-3">
        <Col md={12}>
          <EditString label="Description" value={recurrence.description} onChange={recurrence.setDescription} />
        </Col>
      </Row>
    )}

    <Row className="mb-3">
      <Col md={12}>
        <EditRadioGroup<RecurrenceMode>
          value={recurrence.mode}
          onChange={recurrence.setMode}
          options={[
            {
              key: 'interval',
              value: 'interval',
              label: 'Interval',
              content: (
                <>
                  <Row className="mb-2">
                    <Col md={6}>
                      <EditSelect<IntervalRecurrenceUnit> label="Unit" value={recurrence.unit} onChange={recurrence.setUnit} mandatory options={UNIT_OPTIONS} />
                    </Col>
                    <Col md={6}>
                      <EditNumber label="Interval" value={recurrence.interval} onChange={recurrence.setInterval} mandatory min={1} />
                    </Col>
                  </Row>
                  {/* start_time / start_time_zone are interval-only on the
                      backend — they pin the time-of-day anchor, which the
                      window_open branch never uses. */}
                  <Row>
                    <Col md={6}>
                      <EditTime label="Start Time" value={recurrence.startTime} onChange={recurrence.setStartTime} />
                    </Col>
                    <Col md={6}>
                      <EditTimeZoneReference value={recurrence.startTimeZone} onChange={recurrence.setStartTimeZone} />
                    </Col>
                  </Row>
                </>
              ),
            },
            {
              key: 'window_open',
              value: 'window_open',
              label: 'On Scheduling Window Open',
              content: (
                <Row>
                  <Col md={12}>
                    <EditSchedulingWindowReference value={recurrence.schedulingWindow} onChange={recurrence.setSchedulingWindow} mandatory />
                  </Col>
                </Row>
              ),
            },
          ]}
        />
      </Col>
    </Row>

    {/* during_scheduling_window is mode-agnostic on the backend — a secondary
        gate over whatever next-occurrence the unit branch produced. */}
    <Row className="mb-3">
      <Col md={12}>
        <EditSchedulingWindowReference
          label="During Scheduling Window"
          value={recurrence.duringSchedulingWindow}
          onChange={recurrence.setDuringSchedulingWindow}
        />
      </Col>
    </Row>

    <Row className="mb-3">
      <Col md={12}>
        <EditBoolean
          label="Preserve wall-clock time"
          value={recurrence.preserveTime}
          onChange={recurrence.setPreserveTime}
          helpText="When on, each occurrence keeps the same local clock time when the clocks change for daylight saving (e.g. always 09:00). When off, occurrences are spaced by an exact fixed interval, so the local time can shift by an hour across a DST change."
        />
      </Col>
    </Row>

    <Row className="mb-3">
      <Col md={12}>
        <EditBoolean
          label="Skip when window closed"
          value={recurrence.skipWhenWindowClosed}
          onChange={recurrence.setSkipWhenWindowClosed}
          helpText="When on, intervals that fall outside the scheduling window are skipped. When off, execution is postponed to the next time the window opens."
        />
      </Col>
    </Row>
  </>
));
